import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from 'src/app/services/auth.service';
import { authErrorKeys } from 'src/app/util/auth-error.util';
import { passwordProblems, isMismatch } from '../../../util/password-form.util';

type Stage = 'loading' | 'invalid' | 'password' | 'enrol' | 'done';

/**
 * Redeems an emailed invitation: the invitee sets their own password, then enrols
 * a second factor.
 *
 * Unauthenticated by necessity, so the route carries no guard. Two consequences
 * are handled here rather than left to chance:
 *
 *  - Any token already in localStorage is cleared on entry. util/auth.interceptor.ts
 *    attaches a bearer token to every same-host request, and sending a stale one
 *    to an endpoint that expects none is at best noise; it also means the nav
 *    header (which self-hides on `auth.isloggedIn`) would render logged-in chrome
 *    around an anonymous page.
 *  - The account is NOT usable when the password is set. The server keeps it
 *    INVITED until a second factor is confirmed, so this page always continues
 *    into enrolment and never navigates away in between.
 */
@Component({
    selector: 'app-accept-invitation',
    templateUrl: './accept-invitation.component.html',
    styleUrls: ['../auth-shell.css'],
    standalone: false
})
export class AcceptInvitationComponent implements OnInit {
  stage: Stage = 'loading';
  busy = false;
  errorKeys: string[] = [];

  token = '';
  username = '';
  name = '';
  minLength = 12;
  allowedMethods: string[] = ['totp', 'email'];

  password = '';
  repeat = '';
  challenge = '';

  constructor(private route: ActivatedRoute, private router: Router, private auth: AuthService) {}

  ngOnInit() {
    this.auth.clearLocalStorage();

    this.route.queryParams.subscribe((params) => {
      this.token = params['token'] || '';
      if (!this.token) { this.stage = 'invalid'; return; }

      this.auth.invitationLookup(this.token).subscribe({
        next: (res) => {
          this.username = res.username;
          this.name = res.name || '';
          this.minLength = res.passwordMinLength || 12;
          this.allowedMethods = res.allowedMfaMethods || ['totp', 'email'];
          this.stage = 'password';
        },
        // An expired invitation and an unknown one are the same response by
        // design, so there is one message for both.
        error: () => { this.stage = 'invalid'; }
      });
    });
  }

  get mismatch(): boolean {
    return isMismatch(this.password, this.repeat);
  }

  /**
   * Only `busy` disables the button.
   *
   * It used to also require the length and match rules, which made a valid-looking
   * form produce a button that did nothing and said nothing: the length rule was
   * stated as a note, never as an error, so a short password gave no feedback at
   * all. "I click save and nothing happens" was exactly that.
   *
   * Validation now happens on submit and names the problem. A button you can
   * press and be told why is strictly better than one you cannot press for
   * reasons you must infer.
   */
  get canSubmit(): boolean {
    return !this.busy;
  }

  /** Client-side rules, as translation keys. Empty means submit. */
  private localProblems(): string[] {
    return passwordProblems({
      password: this.password,
      repeat: this.repeat,
      minLength: this.minLength,
      mismatchShown: this.mismatch
    });
  }

  submit() {
    if (this.busy) return;
    // Report every broken rule at once rather than one per attempt.
    const problems = this.localProblems();
    if (problems.length) { this.errorKeys = problems; return; }
    this.busy = true;
    this.errorKeys = [];
    this.auth.invitationAccept(this.token, this.password).subscribe({
      next: (res) => {
        this.busy = false;
        this.challenge = res.challenge;
        this.allowedMethods = res.allowedMfaMethods || this.allowedMethods;
        // Clear the plaintext from the component as soon as it is no longer needed.
        this.password = '';
        this.repeat = '';
        this.stage = 'enrol';
      },
      error: (err) => { this.busy = false; this.errorKeys = authErrorKeys(err); }
    });
  }

  onEnrolled(token: string) {
    this.auth.storeSession(token);
    this.auth.setLoginStatus(true);
    this.auth.setUserEnabled(true);
    this.stage = 'done';
    // Full reload rather than router navigation, matching what the SSO path does
    // after its token exchange: it re-bootstraps app state with the new token.
    window.location.href = '/';
  }
}
